import PageMeta from "components/PageMeta";
import { Account } from "models/Account";
import { Asset, AssetCriticality, AssetStatus } from "models/Asset";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Card, CardBody, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import * as api from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { assetTypeIcon } from "./assetPresentation";

const statusValues: Array<AssetStatus | ""> = ["", "active", "maintenance", "spare", "decommissioned", "failed", "disposed"];
const criticalityValues: Array<AssetCriticality | ""> = ["", "low", "medium", "high", "critical"];
const criticalityColor = (value: AssetCriticality) => value === "critical" ? "danger" : value === "high" ? "warning" : value === "medium" ? "info" : "secondary";

const Assets: React.FC = () => {
    const {t} = useOfficeTranslation();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("");
    const [criticality, setCriticality] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [calendarUrl, setCalendarUrl] = useState("");
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState("");
    const [calendarCopied, setCalendarCopied] = useState(false);

    useEffect(() => {
        Promise.all([api.getAssets(), api.getAccounts("customer")])
            .then(([assetValues, accountValues]) => { setAssets(assetValues); setAccounts(accountValues); })
            .catch(reason => setError(String(reason.response?.data?.error ?? reason)))
            .finally(() => setLoading(false));
    }, []);

    const customerNames = useMemo(() => Object.fromEntries(accounts.map(account => [account.id, account.legal_name])), [accounts]);
    const filtered = useMemo(() => assets.filter(asset =>
        (!query.trim() || [asset.name, asset.type, asset.location, asset.serial_number, asset.manufacturer, asset.model,
            ...asset.custom_properties.flatMap(property => [property.key, property.label, property.unit ?? "", String(property.value ?? "")])]
            .some(value => value?.toLowerCase().includes(query.toLowerCase()))) &&
        (!status || asset.status === status) &&
        (!criticality || asset.criticality === criticality) &&
        (!customerId || asset.customer_id === customerId)
    ), [assets, criticality, customerId, query, status]);

    const activeFilterCount = [status, criticality, customerId].filter(Boolean).length;

    const clearFilters = () => {
        setStatus("");
        setCriticality("");
        setCustomerId("");
    };

    const openCalendar = () => {
        setCalendarOpen(true);
        setCalendarUrl("");
        setCalendarError("");
        setCalendarCopied(false);
        setCalendarLoading(true);
        api.getAssetCalendarLink()
            .then(setCalendarUrl)
            .catch(() => setCalendarError(t("assets.calendar.loadFailed")))
            .finally(() => setCalendarLoading(false));
    };

    const copyCalendarUrl = async () => {
        try {
            await navigator.clipboard.writeText(calendarUrl);
            setCalendarCopied(true);
            setCalendarError("");
        } catch {
            setCalendarError(t("assets.calendar.copyFailed"));
        }
    };

    return <div className="page-content assets-page">
        <PageMeta title={t("assets.title")} />
        <div className="page-title-box assets-page-title d-flex justify-content-between align-items-center gap-3">
            <div className="min-w-0"><h4 className="mb-1">{t("assets.title")}</h4><p className="text-muted mb-0">{t("assets.subtitle")}</p></div>
            <div className="asset-page-actions d-flex flex-shrink-0 gap-2">
                <Button aria-label={t("assets.calendar.action")} className="asset-page-action" color="light" onClick={openCalendar} type="button">
                    <i className="mdi mdi-calendar-sync" aria-hidden="true" /><span className="asset-action-label ms-1">{t("assets.calendar.action")}</span>
                </Button>
                <Link aria-label={t("assets.newAsset")} className="btn btn-primary asset-page-action" to="/assets/add">
                    <i className="mdi mdi-plus" aria-hidden="true" /><span className="asset-action-label ms-1">{t("assets.newAsset")}</span>
                </Link>
            </div>
        </div>
        {error && <Alert color="danger">{error}</Alert>}
        <Card className="assets-list-card">
            <CardBody className="p-0">
                <div className="assets-toolbar">
                    <div className="asset-search-controls">
                        <div className="asset-search-field">
                            <i className="mdi mdi-magnify" aria-hidden="true" />
                            <Input aria-label={t("assets.search")} placeholder={t("assets.search")} value={query} onChange={event => setQuery(event.target.value)} />
                        </div>
                        <Button
                            aria-controls="asset-secondary-filters"
                            aria-expanded={filtersOpen}
                            className="asset-filter-toggle d-xl-none"
                            color={activeFilterCount > 0 ? "primary" : "light"}
                            onClick={() => setFiltersOpen(value => !value)}
                            type="button"
                        >
                            <i className="mdi mdi-tune-variant" aria-hidden="true" />
                            <span>{t("assets.filters")}</span>
                            {activeFilterCount > 0 && <Badge color="light" className="text-primary">{activeFilterCount}</Badge>}
                        </Button>
                    </div>
                    <div className={`asset-filter-fields ${filtersOpen ? "is-open" : ""}`} id="asset-secondary-filters">
                        <Input aria-label={t("assets.allStatuses")} type="select" value={status} onChange={event => setStatus(event.target.value)}>{statusValues.map(value => <option key={value || "all"} value={value}>{value || t("assets.allStatuses")}</option>)}</Input>
                        <Input aria-label={t("assets.allCriticalities")} type="select" value={criticality} onChange={event => setCriticality(event.target.value)}>{criticalityValues.map(value => <option key={value || "all"} value={value}>{value || t("assets.allCriticalities")}</option>)}</Input>
                        <Input aria-label={t("assets.allCustomers")} type="select" value={customerId} onChange={event => setCustomerId(event.target.value)}><option value="">{t("assets.allCustomers")}</option>{accounts.map(account => <option value={account.id} key={account.id}>{account.legal_name}</option>)}</Input>
                    </div>
                    {!loading && <div className="asset-results-row">
                        <span aria-live="polite">{filtered.length === 1 ? t("assets.resultCountSingle") : t("assets.resultCount", {count: filtered.length})}</span>
                        {activeFilterCount > 0 && <Button className="p-0" color="link" onClick={clearFilters} type="button">{t("assets.clearFilters")}</Button>}
                    </div>}
                </div>
                {loading && <div className="text-center py-5"><Spinner /></div>}
                {!loading && filtered.map(asset => {
                    const overdue = asset.service_intervals.filter(interval => interval.overdue).length;
                    const due = asset.service_intervals.filter(interval => interval.due && !interval.overdue).length;
                    const manufacturerModel = [asset.manufacturer, asset.model].filter(Boolean).join(" ");
                    return <Link className="asset-list-item text-body text-decoration-none" to={`/assets/${asset.id}`} key={asset.id}>
                        <span className="asset-list-icon" aria-hidden="true"><i className={`mdi ${assetTypeIcon(asset.type)}`} /></span>
                        <span className="asset-list-identity">
                            <strong>{asset.name}</strong>
                            {manufacturerModel && <small>{manufacturerModel}</small>}
                        </span>
                        <span className="asset-list-details">
                            <span className="text-capitalize"><i className="mdi mdi-shape-outline" aria-hidden="true" />{asset.type.replaceAll("_", " ")}</span>
                            <span><i className="mdi mdi-domain" aria-hidden="true" />{asset.customer_id ? customerNames[asset.customer_id] ?? asset.customer_id : t("assets.internal")}</span>
                            {asset.serial_number && <small>{asset.serial_number}</small>}
                        </span>
                        <span className="asset-list-state">
                            <Badge color={criticalityColor(asset.criticality)} className="text-capitalize">{asset.criticality}</Badge>
                            <span className="badge bg-light text-dark text-capitalize">{asset.status}</span>
                        </span>
                        {(overdue > 0 || due > 0) && <span className="asset-list-maintenance">
                            {overdue > 0 && <Badge color="danger">{overdue} {t("assets.overdue")}</Badge>}
                            {due > 0 && <Badge color="warning">{due} {t("assets.due")}</Badge>}
                        </span>}
                        <span className="asset-list-open" aria-hidden="true"><i className="mdi mdi-chevron-right" /></span>
                    </Link>;
                })}
                {!loading && filtered.length === 0 && <p className="text-muted text-center py-5 mb-0">{t("assets.noAssets")}</p>}
            </CardBody>
        </Card>
        <Modal centered isOpen={calendarOpen} toggle={() => setCalendarOpen(false)}>
            <ModalHeader toggle={() => setCalendarOpen(false)}>{t("assets.calendar.title")}</ModalHeader>
            <ModalBody>
                <p>{t("assets.calendar.help")}</p>
                {calendarError && <Alert color="danger">{calendarError}</Alert>}
                {calendarLoading && <div className="text-center py-3"><Spinner size="sm" /></div>}
                {calendarUrl && <>
                    <Label for="asset-calendar-url">{t("assets.calendar.linkLabel")}</Label>
                    <div className="asset-calendar-link-row">
                        <Input aria-label={t("assets.calendar.linkLabel")} id="asset-calendar-url" onFocus={event => event.currentTarget.select()} readOnly type="url" value={calendarUrl} />
                        <Button color={calendarCopied ? "success" : "primary"} onClick={copyCalendarUrl} type="button">
                            <i className={`mdi ${calendarCopied ? "mdi-check" : "mdi-content-copy"}`} aria-hidden="true" />
                            <span className="ms-1">{t(calendarCopied ? "assets.calendar.copied" : "assets.calendar.copy")}</span>
                        </Button>
                    </div>
                    <small className="text-muted d-block mt-2">{t("assets.calendar.secret")}</small>
                </>}
            </ModalBody>
            <ModalFooter><Button color="light" onClick={() => setCalendarOpen(false)} type="button">{t("common.close")}</Button></ModalFooter>
        </Modal>
    </div>;
};

export default Assets;
